"use client";

import React, { useState, useEffect, useRef } from "react";
import { CircleCheck, ShieldCheck, House, Truck, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const complianceCategories = [{
  id: "health-safety",
  icon: ShieldCheck,
  title: "Health & Safety",
  heading: "Uncompromising Safety Standards",
  description: "At Vantage Support Group, health and safety is the cornerstone of our operational culture. We implement rigorous, industry-specific safety protocols designed to protect our workforce and ensure seamless operations in high-stakes environments.",
  paragraphs: ["Our dedicated on-site management teams conduct regular safety audits, comprehensive risk assessments, and continuous training programs. We believe in a proactive approach to hazard identification and mitigation.", "Compliance with international and local occupational health and safety regulations is strictly monitored and enforced across all our operational sites, ensuring a zero-harm working environment."],
  checklist: ["Daily on-site safety briefings and toolbox talks", "Comprehensive site-specific risk assessments", "Strict adherence to local and international safety regulations", "Continuous safety training and certification programs"]
}, {
  id: "accommodation",
  icon: House,
  title: "Accommodation",
  heading: "Quality Living Standards",
  description: "We recognize that a well-rested and comfortable workforce is a productive one. Vantage Support Group manages high-quality accommodation solutions that meet and exceed legal standards for worker housing.",
  paragraphs: ["Our accommodation facilities are strategically located to minimize commute times and are subjected to rigorous, regular inspections. We ensure all living quarters provide adequate space, hygiene facilities, and comfort.", "From day-to-day management of the facilities to ensuring compliance with local housing authorities, we handle all aspects of worker accommodation, providing peace of mind for both our clients and our personnel."],
  checklist: ["Regular health and hygiene inspections of all facilities", "Compliance with national worker housing regulations", "Managed day-to-day facility operations and maintenance", "Strategic locations to optimize transport logistics"]
}, {
  id: "transport",
  icon: Truck,
  title: "Transport Services",
  heading: "Reliable Logistics & Transit",
  description: "Efficient and safe transportation of personnel is critical to maintaining operational timelines. We provide fully managed transport services tailored to the specific needs of your project site.",
  paragraphs: ["Our transport solutions include fleet management, route optimization, and strict driver compliance checks. We ensure that all vehicles meet safety standards and are regularly maintained.", "By coordinating daily transit schedules, we guarantee punctual arrivals and departures, minimizing downtime and ensuring that workforce logistics never become a bottleneck for your operations."],
  checklist: ["Fully compliant and regularly maintained transport fleet", "Optimized routing for punctual shift changes", "Strict driver qualification and safety monitoring", "Scalable transport solutions for varying workforce sizes"]
}, {
  id: "general-compliance",
  icon: FileCheck,
  title: "General Compliance",
  heading: "Strict Regulatory Adherence",
  description: "Navigating the complex landscape of labor laws and operational regulations is our expertise. Vantage Support Group ensures absolute compliance across all administrative and operational fronts.",
  paragraphs: ["From payroll and salary services to contract management and worker rights, our administrative support teams maintain meticulous records and ensure full transparency in all our dealings.", "We act as a shield for our clients against compliance risks, conducting regular internal audits and maintaining up-to-date knowledge of all relevant industry regulations and legal requirements."],
  checklist: ["Transparent and accurate payroll and salary administration", "Full compliance with national labor laws and worker rights", "Regular internal audits and transparent reporting", "Dedicated legal and administrative support teams"]
}];
export default function OperationalCompliance() {
  const [activeTabId, setActiveTabId] = useState(complianceCategories[0].id);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const activeCategory = complianceCategories.find(c => c.id === activeTabId) || complianceCategories[0];
  return <section id="operational-compliance" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[30%] h-[30%] rounded-full bg-accent/5 blur-[100px]" />
      </div>

      
    </section>;
}